import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Clock, ArrowDown } from "lucide-react";

const HERO_IMG = "/images/sala.jpg";

const lines = ["Cucina piemontese", "in un palazzo", "dell'Ottocento."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative pt-32 sm:pt-40 lg:pt-48 pb-16 lg:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-end">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-mono-accent text-xs uppercase tracking-[0.35em] text-[#B87F1B] mb-8"
              data-testid="hero-eyebrow"
            >
              Ristorante Baracca — Biella
            </motion.p>
            <h1 className="font-serif-display font-medium text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight" data-testid="hero-title">
              {lines.map((line, i) => (
                <span key={line} className="mask-line pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1.1, delay: 0.45 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 2 ? (
                      <em className="text-[#7A1C28]">{line}</em>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="mt-8 max-w-md text-base sm:text-lg leading-relaxed text-[#6E6A66] font-light"
            >
              Pietanze, dolci e vini piemontesi serviti tra arredi sobri e quadri
              alle pareti gialle, nel cuore di Biella.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="tel:01521941"
                data-testid="hero-call-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-[#FAF8F5] px-7 py-3.5 text-sm font-medium hover:bg-[#7A1C28] transition-colors duration-300"
              >
                Prenota un tavolo
              </a>
              <a
                href="#piatti"
                data-testid="hero-dishes-button"
                className="inline-flex items-center gap-2 rounded-full border border-[#1C1917]/20 px-7 py-3.5 text-sm font-medium hover:border-[#1C1917] hover:bg-[#1C1917] hover:text-[#FAF8F5] transition-colors duration-300"
              >
                Scopri i piatti
                <ArrowDown size={14} />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="frame-clip relative aspect-[4/3] max-h-[520px] w-full overflow-hidden"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Sala del Ristorante Baracca in un palazzo dell'Ottocento"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/35 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.5 }}
              className="absolute -bottom-6 -left-4 sm:-left-10 bg-[#FAF8F5]/90 backdrop-blur-md border border-[#E7E4DF] px-6 py-5 shadow-[0_12px_40px_rgba(28,25,23,0.10)]"
              data-testid="hero-info-badge"
            >
              <div className="flex items-center gap-3 font-mono-accent text-xs text-[#1C1917]">
                <Clock size={14} className="text-[#B87F1B]" />
                Lun–Ven · 19:00–21:30
              </div>
              <div className="mt-2 flex items-center gap-3 font-mono-accent text-xs text-[#6E6A66]">
                <MapPin size={14} className="text-[#B87F1B]" />
                Via Sant'Eusebio 12, Biella
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
